import {
  ButtonMenu,
  CollapseToggle,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  InlineFeedbackBanner,
  MenuItem,
  actionColumn,
  column,
  dragHandleColumn,
  emptyCell,
  insertAtIndex,
  numericColumn,
  simpleHeader,
  useComputed,
  useModal,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { camelCase } from "change-case";
import { ReactNode, useRef, useState } from "react";
import { AiNumberField, BoundAiSelectField, BoundAiTextField } from "src/components/formFields/ai";
import { type AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import {
  findAiFieldProposal,
  parseAiNamedOption,
  parseAiNumber,
} from "src/components/formFields/ai/utils/bindAiProposal";
import {
  reconcilePendingAiProposals,
  resolvePendingAiProposals,
} from "src/components/formFields/ai/utils/reconcilePendingAiProposals";
import { AiFieldProposalStatus } from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  PlanPackageLocationsV2Query,
  PlanPackageLocationsV2_LocationFragment,
} from "src/routes/libraries/plan-package-v2/components/LocationsTab.generated";
import { roomProposalBindings } from "src/routes/libraries/plan-package-v2/components/locations/locationProposalUtils";
import {
  LevelGroup,
  RoomFormValue,
  SaveRpls,
  activeOptionsCount,
  describeRoomRange,
  formatFullRoomNumber,
  permuteRoomNumbers,
  validateFullRoomNumber,
} from "src/routes/libraries/plan-package-v2/components/locations/locationUtils";
import { type LocationRowForm } from "./LocationRow";

type LevelLocationsTableProps = {
  // Keep the level metadata, but replace raw room fragments with editable forms backed by LocationRow.
  group: Omit<LevelGroup, "rooms"> & { rooms: LocationRowForm[] };
  roomTypes: PlanPackageLocationsV2Query["roomTypes"];
  disabled: boolean | ReactNode;
  saveRpls: SaveRpls;
  isDuplicateFullRoomNumber: (rplId: string, fullRoomNumber: number) => boolean;
  showLevelDelete: boolean;
};

export function LevelLocationsTable(props: LevelLocationsTableProps) {
  const { group, roomTypes, disabled, saveRpls, isDuplicateFullRoomNumber, showLevelDelete } = props;
  const { openModal } = useModal();
  const levelName = group.location.name;
  const orderedRooms = useComputed(() => group.rooms.sortBy((os) => os.roomNumber.value ?? 0), [group]);
  const rows = useComputed<GridDataRow<Row>[]>(
    () => [
      simpleHeader,
      {
        kind: "level",
        id: group.location.id,
        data: group,
        children: orderedRooms.map((room) => ({
          kind: "room" as const,
          id: room.value.id,
          // FIXME: If we update beam to expose a `removeCompanionRow` cb or something this wont be needed
          // The warning rides along in `data` on purpose: `GridTable` is not an observer and
          // `RowState._row` is not observable, so a companion that merely changes identity is never
          // re-read. `_data` is `observableRef` and is what `RowState.row` hangs its reactivity on,
          // so putting the warning here is what lets the banner clear.
          data: { room, warning: room.value.warning },
          draggable: !disabled,
          aiMode: () => room.value.hasPendingProposals,
          companion: companionFor(room.value.warning),
        })),
      },
    ],
    [group, orderedRooms, disabled],
  );

  function companionFor(message: string | undefined) {
    return message ? () => <InlineFeedbackBanner type="warning" description={message} /> : undefined;
  }

  function onRowDrop(dragged: GridDataRow<Row>, dropped: GridDataRow<Row>, indexOffset: number) {
    // Rooms can only be reordered against other rooms, and dropping a row onto itself is a noop
    if (dragged.kind !== "room" || dropped.kind !== "room" || dragged.id === dropped.id) return;
    const currentIds = orderedRooms.map((os) => os.value.id);
    if (!currentIds.includes(dragged.id) || !currentIds.includes(dropped.id)) return;
    const withoutDragged = currentIds.filter((id) => id !== dragged.id);
    const newOrderIds = insertAtIndex(withoutDragged, dragged.id, withoutDragged.indexOf(dropped.id) + indexOffset);
    // Reassign the level's existing room numbers to the new visual order, keeping only the rooms that changed
    const changes = permuteRoomNumbers(
      orderedRooms.map((os) => ({ id: os.value.id, roomNumber: os.roomNumber.value ?? 0 })),
      newOrderIds,
    );
    if (changes.isEmpty) return;
    const handleSave = () => saveRpls(changes);
    const changedRpls = changes.map((change) => orderedRooms.find((os) => os.value.id === change.id)!.value.rpl);
    // Renumbering cascades to attached TLIs and options, so get confirmation first
    if (changedRpls.some((rpl) => rpl.relatedTakeoffLineItemsCount > 0 || activeOptionsCount(rpl) > 0)) {
      const fullRoomNumbers = changedRpls.map((rpl) => rpl.fullRoomNumber!);
      openModal({
        content: (
          <ConfirmationModal
            title="Update Room Numbers"
            label="Update"
            confirmationMessage={updateRoomNumbersMessage(describeRoomRange(levelName, fullRoomNumbers))}
            onConfirmAction={handleSave}
          />
        ),
      });
    } else {
      void handleSave();
    }
  }

  function openDeleteRoomModal(rpl: PlanPackageLocationsV2_LocationFragment) {
    openModal({
      content: (
        <ConfirmationModal
          title="Delete Room"
          label="Delete"
          danger
          confirmationMessage={
            <div css={Css.df.fdc.gap2.$}>
              <p>Are you sure you want to delete this location?</p>
              <p>
                Deleting this location will remove it from {rpl.relatedTakeoffLineItemsCount} takeoff lines and{" "}
                {activeOptionsCount(rpl)} options.
              </p>
            </div>
          }
          onConfirmAction={() => saveRpls([{ id: rpl.id, delete: true }], { membershipChanged: true })}
        />
      ),
    });
  }

  function openDeleteLevelModal() {
    openModal({
      content: (
        <ConfirmationModal
          title="Delete Level"
          label="Delete"
          danger
          confirmationMessage="This level, all its rooms, and all associated items in the Takeoff will be removed."
          onConfirmAction={() => saveRpls([{ id: group.levelRpl!.id, delete: true }], { membershipChanged: true })}
        />
      ),
    });
  }

  /**
   * AI actions come first and carry the sparkle, so they read as AI affordances.
   *
   * TODO: Beam's `MenuItem` needs an `aiMode` prop to auto insert the aiStar and color the label.
   */
  function roomMenuItems(os: LocationRowForm): MenuItem[] {
    const { rpl, fieldProposals } = os.value;
    function reconcile(decision: AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected) {
      return () => {
        reconcilePendingAiProposals(roomProposalBindings(os, levelName, fieldProposals, roomTypes), decision);
        resolvePendingAiProposals(fieldProposals, decision);
      };
    }
    return [
      { label: "Accept Changes", icon: "aiStar", onClick: reconcile(AiFieldProposalStatus.Accepted) },
      { label: "Ignore Change", icon: "aiStar", onClick: reconcile(AiFieldProposalStatus.Rejected) },
      {
        label: "View Import Source",
        icon: "aiStar",
        // TODO: PLAN-1822 — wire this up like the Program Data tab does, off `afp.pageNumbers`.
        onClick: () => alert("View Import Source coming soon"),
      },
      {
        label: "Remove Room",
        onClick: () => openDeleteRoomModal(rpl),
        disabled: !!disabled || disableBasedOnPotentialOperation(rpl.canDelete),
      },
    ];
  }

  const columns: GridColumn<Row>[] = [
    dragHandleColumn<Row>({
      header: { content: "Order", colspan: 2, alignment: "left" },
      level: (_, { row }) => ({ content: () => <CollapseToggle row={row} compact /> }),
    }),
    numericColumn<Row>({
      header: emptyCell,
      level: () => ({ content: () => <span css={Css.smSb.$}>{levelName}</span>, colspan: 3, alignment: "left" }),
      room: (data) => ({
        content: () => (
          <OrderCell
            rpl={data.room.value.rpl}
            os={data.room}
            levelName={levelName}
            disabled={disabled}
            isDuplicateFullRoomNumber={isDuplicateFullRoomNumber}
            proposal={findAiFieldProposal(data.room.value.fieldProposals, "roomNumber")}
          />
        ),
      }),
      w: "80px",
    }),
    column<Row>({
      header: "Room",
      level: emptyCell,
      room: (data) => {
        const { room } = data;
        const { rpl, fieldProposals } = room.value;
        return (
          <BoundAiSelectField
            field={room.roomTypeId}
            label="Room"
            labelStyle="hidden"
            options={roomTypes}
            unsetLabel={rpl.location.name}
            disabled={disabled}
            aiProposal={findAiFieldProposal(fieldProposals, "roomType")}
            parser={parseAiNamedOption(roomTypes)}
          />
        );
      },
      // TODO: This column must be will have
      w: 2,
    }),
    column<Row>({
      header: "Marketing Name",
      level: emptyCell,
      room: (data) => {
        const { room } = data;
        const { fieldProposals } = room.value;
        return (
          <BoundAiTextField
            field={room.marketingName}
            label="Marketing Name"
            labelStyle="hidden"
            disabled={disabled}
            aiProposal={findAiFieldProposal(fieldProposals, "marketingName")}
          />
        );
      },
    }),
    actionColumn<Row>({
      header: emptyCell,
      level: () =>
        showLevelDelete ? (
          <IconButton icon="trash" label="Delete Level" onClick={openDeleteLevelModal} disabled={disabled} inc={2.25} />
        ) : (
          emptyCell
        ),
      room: (data) => {
        const { room } = data;
        return room.value.proposal ? (
          <ButtonMenu trigger={{ icon: "verticalDots" }} placement="right" items={roomMenuItems(room)} />
        ) : (
          <IconButton
            icon="trash"
            inc={2.25}
            onClick={() => openDeleteRoomModal(room.value.rpl)}
            disabled={disabled || disableBasedOnPotentialOperation(room.value.rpl.canDelete)}
          />
        );
      },
      w: "56px",
    }),
  ];

  return (
    <GridTable
      id={`${camelCase(levelName, { mergeAmbiguousCharacters: true })}Table`} // "Level 1" -> level1Table
      columns={columns}
      rows={rows}
      onRowDrop={onRowDrop}
      style={{ bordered: true, allWhite: true, inlineEditing: true, rowHeight: "fixed" }}
    />
  );
}

type HeaderRow = { kind: "header"; id: string; data: undefined };
type LevelRow = { kind: "level"; id: string; data: LevelLocationsTableProps["group"] };
/** The room plus its derived warning, so a changed warning changes the row's `data` identity. */
type RoomRowData = { room: LocationRowForm; warning: string | undefined };
type RoomRow = { kind: "room"; id: string; data: RoomRowData };
type Row = HeaderRow | LevelRow | RoomRow;

type OrderCellProps = {
  rpl: PlanPackageLocationsV2_LocationFragment;
  os: ObjectState<RoomFormValue>;
  levelName: string;
  disabled: boolean | ReactNode;
  isDuplicateFullRoomNumber: (rplId: string, fullRoomNumber: number) => boolean;
  proposal: ObjectState<AiFieldProposalFormValue> | undefined;
};

/**
 * An unbound field over `os.roomNumber` so typed values only commit on blur, after validation and
 * (when takeoff lines or options are attached) the Update Room Numbers confirmation.
 *
 * Stays unbound rather than becoming a `BoundAiNumberField` because that confirmation has to run
 * before the value lands; `AiNumberField` handles only the proposal display.
 */
function OrderCell(props: OrderCellProps) {
  const { rpl, os, levelName, disabled, isDuplicateFullRoomNumber, proposal } = props;
  const { openModal } = useModal();
  const [pending, setPending] = useState<number>();
  // Mirrors `pending` for commit() to read: blur can fire before the state update has rendered (e.g. paste, then tab)
  const pendingRef = useRef<number>();
  const [errorMsg, setErrorMsg] = useState<string>();
  const savedFullRoomNumber = useComputed(
    () => (os.roomNumber.value ? formatFullRoomNumber(levelName, os.roomNumber.value) : undefined),
    [os, levelName],
  );

  function commit() {
    const typed = pendingRef.current;
    if (typed === undefined) return;
    const validated = validateFullRoomNumber(levelName, typed);
    if ("error" in validated) return setErrorMsg(validated.error);
    const { roomNumber } = validated;
    if (isDuplicateFullRoomNumber(rpl.id, typed)) return setErrorMsg("Duplicate room number");
    pendingRef.current = undefined;
    setPending(undefined);
    // Editing the cell resolves its proposal either way — matching the typed value accepts it, anything
    // else rejects it — so this has to run even when the value itself is unchanged.
    function apply() {
      proposal?.status.set(
        parseAiNumber(proposal.fieldValue.value) === typed
          ? AiFieldProposalStatus.Accepted
          : AiFieldProposalStatus.Rejected,
      );
      os.roomNumber.set(roomNumber);
    }
    if (
      validated.roomNumber !== os.roomNumber.value &&
      (rpl.relatedTakeoffLineItemsCount > 0 || activeOptionsCount(rpl) > 0)
    ) {
      openModal({
        content: (
          <ConfirmationModal
            title="Update Room Numbers"
            label="Update"
            confirmationMessage={updateRoomNumbersMessage(describeRoomRange(levelName, [savedFullRoomNumber!]))}
            onConfirmAction={apply}
          />
        ),
      });
    } else {
      apply();
    }
  }

  return (
    <AiNumberField
      label="Order"
      labelStyle="hidden"
      value={pending ?? savedFullRoomNumber}
      originalValue={rpl.fullRoomNumber ?? null}
      // The proposal holds the full room number the AI read off the drawing, which is what this cell shows.
      aiProposal={proposal}
      onChange={(value) => {
        pendingRef.current = value;
        setPending(value);
        setErrorMsg(undefined);
      }}
      onBlur={commit}
      errorMsg={errorMsg}
      errorInTooltip
      positiveOnly
      disabled={disabled}
    />
  );
}

function updateRoomNumbersMessage(roomRange: string) {
  return (
    <div css={Css.df.fdc.gap2.$}>
      <p>
        Are you sure you want to update the room numbers for {roomRange}? Be sure these room numbers match the latest
        construction documents.
      </p>
      <p>
        Options and takeoff lines assigned to these room numbers will also be updated to reflect the updated room
        numbers.
      </p>
    </div>
  );
}
